import React, { useState, useEffect } from "react";

const INITIAL_SEDE = { codigo_bodega: "", nombre: "", prefijo: "", estado: "ACTIVO" };

const INITIAL_VEHICULO = {
  placa: "",
  tipo: "FURGON",
  clase_vehiculo: "CAMION",
  marca: "CHEVROLET",
  linea: "",
  modelo: "",
  color: "",
  ciudad: "FUSAGASUGA",
  vin: "",
  numero_motor: "",
  licencia_transito: "",
  matricula: "",
  kilometraje_actual: 0,
  soat_vencimiento: "",
  tecno_vencimiento: "",
  mantenimiento_vencimiento: "",
  sede_id: "",
  estado: "ACTIVO"
};

export default function GestionSedes() {
  const [tab, setTab] = useState("sedes");
  const [sedes, setSedes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estados de formulario y edición para Sedes
  const [formSede, setFormSede] = useState(INITIAL_SEDE);
  const [editandoSedeId, setEditandoSedeId] = useState(null);

  // Estados de formulario y edición para Vehículos
  const [formVehiculo, setFormVehiculo] = useState(INITIAL_VEHICULO);
  const [editandoVehiculoId, setEditandoVehiculoId] = useState(null);

  const [mensaje, setMensaje] = useState({ text: "", type: "" });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const [resSedes, resVeh] = await Promise.all([
        fetch(`${API_URL}/api/usuarios/sedes`),
        fetch(`${API_URL}/api/usuarios/vehiculos`)
      ]);
      const dataSedes = await resSedes.json();
      const dataVeh = await resVeh.json();
      if (dataSedes.success) setSedes(dataSedes.data || []);
      if (dataVeh.success) setVehiculos(dataVeh.data || []);
    } catch (e) {
      console.error("Error cargando sedes/vehículos:", e);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    return String(dateStr).split("T")[0];
  };

  // ==========================================
  // LÓGICA DE SEDES (CREAR / EDITAR / ELIMINAR)
  // ==========================================
  const handleSubmitSede = async (e) => {
    e.preventDefault();
    if (!formSede.codigo_bodega || !formSede.nombre) return;
    setMensaje({ text: "", type: "" });

    try {
      const url = editandoSedeId
        ? `${process.env.REACT_APP_API_URL}/api/usuarios/sedes/${editandoSedeId}`
        : `${process.env.REACT_APP_API_URL}/api/usuarios/sedes`;
      const method = editandoSedeId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formSede)
      });
      const data = await res.json();

      if (data.success) {
        setMensaje({
          text: editandoSedeId ? "Sede actualizada correctamente ✅" : "Sede registrada correctamente ✅",
          type: "success"
        });
        setFormSede(INITIAL_SEDE);
        setEditandoSedeId(null);
        cargarDatos();
      } else {
        setMensaje({ text: data.message || "Error al guardar sede", type: "error" });
      }
    } catch (e) {
      setMensaje({ text: "Error de conexión con el servidor", type: "error" });
    }
  };

  const seleccionarSedeParaEditar = (sede) => {
    setEditandoSedeId(sede.id);
    setFormSede({
      codigo_bodega: sede.codigo_bodega || "",
      nombre: sede.nombre || "",
      prefijo: sede.prefijo || "",
      estado: sede.estado || "ACTIVO"
    });
    setMensaje({ text: "", type: "" });
  };

  const cancelarEdicionSede = () => {
    setEditandoSedeId(null);
    setFormSede(INITIAL_SEDE);
  };

  const handleEliminarSede = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta sede?")) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/sedes/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setMensaje({ text: "Sede eliminada correctamente.", type: "success" });
        if (editandoSedeId === id) cancelarEdicionSede();
        cargarDatos();
      } else {
        setMensaje({ text: data.message || "No se pudo eliminar", type: "error" });
      }
    } catch (e) {
      setMensaje({ text: "Error al eliminar sede", type: "error" });
    }
  };

  // ==========================================
  // LÓGICA DE VEHÍCULOS (CREAR / EDITAR / ELIMINAR)
  // ==========================================
  const handleSubmitVehiculo = async (e) => {
    e.preventDefault();
    if (!formVehiculo.placa) return;
    setMensaje({ text: "", type: "" });

    try {
      const url = editandoVehiculoId
        ? `${process.env.REACT_APP_API_URL}/api/usuarios/vehiculos/${editandoVehiculoId}`
        : `${process.env.REACT_APP_API_URL}/api/usuarios/vehiculos`;
      const method = editandoVehiculoId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formVehiculo)
      });
      const data = await res.json();

      if (data.success) {
        setMensaje({
          text: editandoVehiculoId ? "Vehículo actualizado correctamente 🚛" : "Vehículo añadido a la flota 🚛",
          type: "success"
        });
        setFormVehiculo(INITIAL_VEHICULO);
        setEditandoVehiculoId(null);
        cargarDatos();
      } else {
        setMensaje({ text: data.message || "Error al guardar vehículo", type: "error" });
      }
    } catch (e) {
      setMensaje({ text: "Error de conexión al guardar vehículo", type: "error" });
    }
  };

  const seleccionarVehiculoParaEditar = (v) => {
    setEditandoVehiculoId(v.id);
    setFormVehiculo({
      placa: v.placa || "",
      tipo: v.tipo || "FURGON",
      clase_vehiculo: v.clase_vehiculo || "CAMION",
      marca: v.marca || "",
      linea: v.linea || "",
      modelo: v.modelo || "",
      color: v.color || "",
      ciudad: v.ciudad || "FUSAGASUGA",
      vin: v.vin || "",
      numero_motor: v.numero_motor || "",
      licencia_transito: v.licencia_transito || "",
      matricula: v.matricula || "",
      kilometraje_actual: v.kilometraje_actual || 0,
      soat_vencimiento: formatDateForInput(v.soat_vencimiento),
      tecno_vencimiento: formatDateForInput(v.tecno_vencimiento),
      mantenimiento_vencimiento: formatDateForInput(v.mantenimiento_vencimiento),
      sede_id: v.sede_id || "",
      estado: v.estado || "ACTIVO"
    });
    setMensaje({ text: "", type: "" });
  };

  const cancelarEdicionVehiculo = () => {
    setEditandoVehiculoId(null);
    setFormVehiculo(INITIAL_VEHICULO);
  };

  const handleEliminarVehiculo = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este vehículo de la base de datos?")) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/vehiculos/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setMensaje({ text: "Vehículo eliminado.", type: "success" });
        if (editandoVehiculoId === id) cancelarEdicionVehiculo();
        cargarDatos();
      }
    } catch (e) {
      setMensaje({ text: "Error al eliminar vehículo", type: "error" });
    }
  };

  return (
    <div className="w-full flex flex-col gap-6 animate-fade-in">
      {/* PESTAÑAS Y BOTÓN REFRESCAR */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex bg-slate-100 p-1.5 rounded-2xl w-fit border border-slate-200">
          <button
            onClick={() => { setTab("sedes"); setMensaje({ text: "", type: "" }); }}
            className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition cursor-pointer ${
              tab === "sedes" ? "bg-white text-cyan-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            🏢 Sedes y Bodegas ({sedes.length})
          </button>
          <button
            onClick={() => { setTab("vehiculos"); setMensaje({ text: "", type: "" }); }}
            className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition cursor-pointer ${
              tab === "vehiculos" ? "bg-white text-cyan-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            🚛 Flota de Vehículos ({vehiculos.length})
          </button>
        </div>

        <button
          type="button"
          onClick={cargarDatos}
          disabled={loading}
          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-[11px] font-black uppercase tracking-wider transition cursor-pointer flex items-center gap-2 disabled:opacity-50"
        >
          <span className={loading ? "animate-spin" : ""}>🔄</span>
          {loading ? "Actualizando..." : "Refrescar"}
        </button>
      </div>

      {mensaje.text && (
        <div className={`p-4 rounded-2xl text-xs font-bold flex items-center justify-between animate-fade-in-down ${
          mensaje.type === "success" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"
        }`}>
          <span>{mensaje.text}</span>
          <button onClick={() => setMensaje({ text: "", type: "" })} className="font-black cursor-pointer px-2">✕</button>
        </div>
      )}

      {/* ========================================== */}
      {/* VISTA 1: SEDES Y BODEGAS                   */}
      {/* ========================================== */}
      {tab === "sedes" ? (
        <div className="flex flex-col lg:flex-row gap-8">
          <form onSubmit={handleSubmitSede} className="w-full lg:w-1/3 bg-slate-50/90 p-6 rounded-3xl border border-slate-200/90 space-y-4 h-fit shadow-sm">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">
                {editandoSedeId ? `✏️ Editando Sede #${editandoSedeId}` : "➕ Nueva Sede / Bodega"}
              </h4>
              {editandoSedeId && (
                <button type="button" onClick={cancelarEdicionSede} className="text-[10px] font-black text-rose-600 hover:underline cursor-pointer uppercase">
                  Cancelar
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Código Bodega</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: 1, 10, 20..."
                  value={formSede.codigo_bodega}
                  onChange={(e) => setFormSede({ ...formSede, codigo_bodega: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Prefijo (Opcional)</label>
                <input
                  type="text"
                  placeholder="Ej: GC, BAL..."
                  value={formSede.prefijo}
                  onChange={(e) => setFormSede({ ...formSede, prefijo: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 uppercase focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Nombre de la Sede</label>
              <input
                type="text"
                required
                placeholder="Ej: GRAN COLOMBIA..."
                value={formSede.nombre}
                onChange={(e) => setFormSede({ ...formSede, nombre: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 uppercase focus:outline-none focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Estado</label>
              <select
                value={formSede.estado}
                onChange={(e) => setFormSede({ ...formSede, estado: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 cursor-pointer focus:outline-none focus:border-cyan-500"
              >
                <option value="ACTIVO">🟢 ACTIVO</option>
                <option value="INACTIVO">🔴 INACTIVO</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3.5 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-md transition cursor-pointer disabled:opacity-50 ${
                editandoSedeId ? "bg-amber-500 hover:bg-amber-600" : "bg-cyan-600 hover:bg-cyan-700"
              }`}
            >
              {editandoSedeId ? "💾 Actualizar Sede" : "Guardar Sede"}
            </button>
          </form>

          <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto p-1">
            {sedes.map((s) => (
              <div
                key={s.id}
                className={`bg-white p-5 rounded-2xl border transition flex items-center justify-between shadow-sm ${
                  editandoSedeId === s.id ? "border-amber-400 ring-2 ring-amber-400/20" : "border-slate-200/80 hover:border-cyan-300"
                }`}
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-xl bg-cyan-50 text-cyan-700 font-black text-xs flex items-center justify-center border border-cyan-100">
                    #{s.codigo_bodega}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h5 className="font-black text-slate-800 text-sm uppercase">{s.nombre}</h5>
                      <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${
                        s.estado === "INACTIVO" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
                      }`}>
                        {s.estado || "ACTIVO"}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 font-bold mt-0.5">
                      ID Interno: {s.id} {s.prefijo ? `• Prefijo: ${s.prefijo}` : ""}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => seleccionarSedeParaEditar(s)}
                    className="px-3 py-1.5 bg-amber-50 hover:bg-amber-500 text-amber-700 hover:text-white rounded-xl text-[10px] font-black transition cursor-pointer border border-amber-200"
                    title="Editar sede"
                  >
                    ✏️ Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEliminarSede(s.id)}
                    className="px-3 py-1.5 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white rounded-xl text-[10px] font-black transition cursor-pointer border border-rose-200"
                    title="Eliminar sede"
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* ========================================== */
        /* VISTA 2: FLOTA DE VEHÍCULOS                */
        /* ========================================== */
        <div className="flex flex-col xl:flex-row gap-8">
          <form onSubmit={handleSubmitVehiculo} className="w-full xl:w-5/12 bg-slate-50/90 p-6 rounded-3xl border border-slate-200/90 space-y-4 h-fit shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest">
                {editandoVehiculoId ? `✏️ Editando Vehículo: ${formVehiculo.placa}` : "➕ Registrar Vehículo en Flota"}
              </h4>
              {editandoVehiculoId && (
                <button type="button" onClick={cancelarEdicionVehiculo} className="text-[10px] font-black text-rose-600 hover:underline cursor-pointer uppercase">
                  Cancelar Edición
                </button>
              )}
            </div>

            {/* Fila 1: Placa, Tipo, Clase */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Placa *</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: THV951"
                  value={formVehiculo.placa}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, placa: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-black uppercase text-slate-800"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Tipo *</label>
                <input
                  type="text"
                  list="tipos-vehiculo"
                  placeholder="FURGON, NPR..."
                  value={formVehiculo.tipo}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, tipo: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800"
                />
                <datalist id="tipos-vehiculo">
                  <option value="FURGON" />
                  <option value="NPR" />
                  <option value="NHR" />
                  <option value="NKR" />
                  <option value="CAMIONETA" />
                  <option value="MOTO" />
                </datalist>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Clase</label>
                <input
                  type="text"
                  placeholder="CAMION..."
                  value={formVehiculo.clase_vehiculo}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, clase_vehiculo: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800"
                />
              </div>
            </div>

            {/* Fila 2: Marca, Línea, Modelo, Color */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Marca</label>
                <input
                  type="text"
                  placeholder="CHEVROLET, HINO..."
                  value={formVehiculo.marca}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, marca: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Línea</label>
                <input
                  type="text"
                  placeholder="NHR, NKR..."
                  value={formVehiculo.linea}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, linea: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Modelo (Año)</label>
                <input
                  type="number"
                  placeholder="2021"
                  value={formVehiculo.modelo}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, modelo: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Color</label>
                <input
                  type="text"
                  placeholder="BLANCO..."
                  value={formVehiculo.color}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, color: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800"
                />
              </div>
            </div>

            {/* Fila 3: VIN, Número de Motor, Kilometraje */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">VIN / Chasis</label>
                <input
                  type="text"
                  placeholder="9GDNLR..."
                  value={formVehiculo.vin}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, vin: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">N° Motor</label>
                <input
                  type="text"
                  placeholder="4HK1..."
                  value={formVehiculo.numero_motor}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, numero_motor: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Kilometraje Actual</label>
                <input
                  type="number"
                  placeholder="0"
                  value={formVehiculo.kilometraje_actual}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, kilometraje_actual: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                />
              </div>
            </div>

            {/* Fila 4: Fechas de Vencimiento (SOAT, Tecno, Mantenimiento) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white p-3 rounded-2xl border border-slate-200/80">
              <div>
                <label className="block text-[9px] font-black text-slate-500 uppercase mb-1">Vencimiento SOAT</label>
                <input
                  type="date"
                  value={formVehiculo.soat_vencimiento}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, soat_vencimiento: e.target.value })}
                  className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700"
                />
              </div>
              <div>
                <label className="block text-[9px] font-black text-slate-500 uppercase mb-1">Venc. Tecnomecánica</label>
                <input
                  type="date"
                  value={formVehiculo.tecno_vencimiento}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, tecno_vencimiento: e.target.value })}
                  className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700"
                />
              </div>
              <div>
                <label className="block text-[9px] font-black text-slate-500 uppercase mb-1">Próx. Mantenimiento</label>
                <input
                  type="date"
                  value={formVehiculo.mantenimiento_vencimiento}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, mantenimiento_vencimiento: e.target.value })}
                  className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700"
                />
              </div>
            </div>

            {/* Fila 5: Ciudad, Sede Asignada, Estado */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Ciudad Base</label>
                <input
                  type="text"
                  value={formVehiculo.ciudad}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, ciudad: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Sede Asignada</label>
                <select
                  value={formVehiculo.sede_id}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, sede_id: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 cursor-pointer"
                >
                  <option value="">-- General / Sin Sede --</option>
                  {sedes.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Estado</label>
                <select
                  value={formVehiculo.estado}
                  onChange={(e) => setFormVehiculo({ ...formVehiculo, estado: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 cursor-pointer"
                >
                  <option value="ACTIVO">🟢 ACTIVO</option>
                  <option value="MANTENIMIENTO">🟠 EN TALLER</option>
                  <option value="INACTIVO">🔴 INACTIVO</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3.5 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-md transition cursor-pointer disabled:opacity-50 ${
                editandoVehiculoId ? "bg-amber-500 hover:bg-amber-600" : "bg-cyan-600 hover:bg-cyan-700"
              }`}
            >
              {editandoVehiculoId ? "💾 Guardar Cambios del Vehículo" : "🚛 Añadir Vehículo a la Flota"}
            </button>
          </form>

          {/* LISTA DE VEHÍCULOS */}
          <div className="w-full xl:w-7/12 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto p-1">
            {vehiculos.map((v) => (
              <div
                key={v.id}
                className={`bg-white p-5 rounded-2xl border transition flex flex-col justify-between gap-4 shadow-sm ${
                  editandoVehiculoId === v.id ? "border-amber-400 ring-2 ring-amber-400/20" : "border-slate-200/80 hover:border-cyan-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-700 font-black text-xl flex items-center justify-center border border-amber-200">
                      🚛
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h5 className="font-black text-slate-900 text-base uppercase tracking-tight">{v.placa}</h5>
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 uppercase">
                          {v.tipo || "FURGON"}
                        </span>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${
                          v.estado === "INACTIVO" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
                        }`}>
                          {v.estado || "ACTIVO"}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-600 font-bold uppercase mt-0.5">
                        {v.marca || "SIN MARCA"} {v.linea || ""} {v.modelo ? `(${v.modelo})` : ""}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100 text-[10px] font-bold text-slate-600">
                  <div>📍 Ciudad: <span className="text-slate-800">{v.ciudad || "FUSAGASUGA"}</span></div>
                  <div>🛣️ Km: <span className="text-slate-800">{Number(v.kilometraje_actual || 0).toLocaleString()} km</span></div>
                  <div>📄 SOAT: <span className="text-slate-800">{formatDateForInput(v.soat_vencimiento) || "No reg."}</span></div>
                  <div>🔧 Tecno: <span className="text-slate-800">{formatDateForInput(v.tecno_vencimiento) || "No reg."}</span></div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <span className="text-[10px] text-slate-400 font-bold uppercase">
                    {v.sede_nombre ? `🏢 ${v.sede_nombre}` : `VIN: ${v.vin || "N/A"}`}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => seleccionarVehiculoParaEditar(v)}
                      className="px-3 py-1.5 bg-amber-50 hover:bg-amber-500 text-amber-700 hover:text-white rounded-xl text-[10px] font-black transition cursor-pointer border border-amber-200"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEliminarVehiculo(v.id)}
                      className="px-3 py-1.5 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white rounded-xl text-[10px] font-black transition cursor-pointer border border-rose-200"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}